import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { triggerBrowserDownload } from "../utils/triggerDownload";

const fetchPlanSummary = async (planId) => {
  const { data } = await axios.get(`/plans/${planId}/summary`);
  return data;
};

const addInfluencers = async ({ planId, listId, platform, profiles, confirmedUsernames }) => {
  const { data } = await axios.post(`/plans/${planId}/influencers`, {
    listId,
    platform,
    profiles,
    confirmedUsernames,
  });
  return data;
};

// Same endpoint as addInfluencers, for influencers already known by _id (e.g.
// selected from the discovery page's own RapidInfluencer-backed table) — the
// backend skips re-resolving them from a raw handle entirely. See AddToPlanDrawer.
const addInfluencersByRapidId = async ({ planId, listId, platform, rapidInfluencerIds, confirmedUsernames }) => {
  const { data } = await axios.post(`/plans/${planId}/influencers`, {
    listId,
    platform,
    rapidInfluencerIds,
    confirmedUsernames,
  });
  return data;
};

const createList = async ({ planId, name, platform }) => {
  const { data } = await axios.post(`/plans/${planId}/lists`, { name, platform });
  return data;
};

const deleteList = async ({ planId, listId }) => {
  const { data } = await axios.delete(`/plans/${planId}/lists/${listId}`);
  return data;
};

const removeInfluencer = async ({ planId, influencerId }) => {
  const { data } = await axios.delete(`/plans/${planId}/influencers/${influencerId}`);
  return data;
};

// Axios needs the body passed as config.data on a DELETE — it's not a
// positional argument like on post/patch.
const removeInfluencers = async ({ planId, influencerIds }) => {
  const { data } = await axios.delete(`/plans/${planId}/influencers`, { data: { influencerIds } });
  return data;
};

const fetchWhatsappTemplates = async (status) => {
  const { data } = await axios.get(`/whatsapp-templates${status ? `?status=${status}` : ""}`);
  return data;
};

const sendBulkWhatsappMessage = async ({ planId, influencerIds, templateId, variables, campaignName }) => {
  const { data } = await axios.post(`/plans/${planId}/influencers/message`, {
    influencerIds,
    templateId,
    variables,
    campaignName,
  });
  return data;
};

// Records real WhatsApp consent for the given influencers — see the endpoint's own
// comment (routes/plans.js) for why this exists instead of just trusting a checkbox.
// Pass either influencerIds (a specific selection) or all:true (the whole plan — used
// when confirming consent for a "plan"-audience Campaign, whose live membership isn't a
// fixed id set at creation time).
const optInInfluencersWhatsapp = async ({ planId, influencerIds, all }) => {
  const { data } = await axios.post(`/plans/${planId}/influencers/whatsapp-opt-in`, all ? { all: true } : { influencerIds });
  return data;
};

// Backing data for the "View WhatsApp Msg" screen — every conversation belonging to an
// influencer currently on this plan (see PlanWhatsAppPage). Gated the same way as every
// other plan route (org membership), not admin-only like the global WhatsApp Inbox.
const fetchPlanWhatsappConversations = async (planId) => {
  const { data } = await axios.get(`/plans/${planId}/whatsapp-conversations`);
  return data;
};

const fetchPlanWhatsappThread = async ({ planId, conversationId }) => {
  const { data } = await axios.get(`/plans/${planId}/whatsapp-conversations/${conversationId}`);
  return data;
};

const sendPlanWhatsappMessage = async ({ planId, conversationId, text }) => {
  const { data } = await axios.post(`/plans/${planId}/whatsapp-conversations/${conversationId}/send`, { text });
  return data;
};

// Sends from the CALLER'S OWN idiotic.media mailbox (domain-wide delegation — see
// pluginbackendnode/helpers/googleServiceAccountAuth.js), not a shared account. Mirrors
// the WhatsApp bulk-send's own auto-create-a-campaign behavior: this creates a manual-
// audience EmailCampaign under the hood (see routes/plans.js), so it shows up with full
// history on the Email Campaigns page too, not just here. Async: resolves as soon as the
// send is QUEUED (202) — poll the returned campaignId/runId via useEmail.js's
// useEmailCampaign, same "enqueue now, poll separately" split as
// useTriggerRefreshAll/useRefreshAllStatus.
const sendBulkEmail = async ({ planId, influencerIds, subject, message, attachments }) => {
  const { data } = await axios.post(`/plans/${planId}/influencers/email`, {
    influencerIds,
    subject,
    message,
    attachments,
  });
  return data;
};

// Uploads one file ahead of the actual send — EmailBulkSendModal.jsx calls this per
// attached file as soon as it's picked (not deferred to Send time), so the compose form
// can show upload progress/errors per file rather than one opaque wait at the end.
export const uploadEmailAttachment = async (planId, file, onProgress) => {
  const form = new FormData();
  form.append("file", file);
  const { data } = await axios.post(`/plans/${planId}/email-attachments`, form, {
    headers: { "Content-Type": "multipart/form-data" },
    onUploadProgress: onProgress
      ? (evt) => onProgress(evt.total ? Math.round((evt.loaded / evt.total) * 100) : 0)
      : undefined,
  });
  return data;
};

// Backing data for the "View Emails" screen — every email thread belonging to an
// influencer currently on this plan (see PlanEmailPage), same shape/gating as
// fetchPlanWhatsappConversations above.
const fetchPlanEmailConversations = async (planId) => {
  const { data } = await axios.get(`/plans/${planId}/email-conversations`);
  return data;
};

const fetchPlanEmailThread = async ({ planId, conversationId }) => {
  const { data } = await axios.get(`/plans/${planId}/email-conversations/${conversationId}`);
  return data;
};

const sendPlanEmailReply = async ({ planId, conversationId, message }) => {
  const { data } = await axios.post(`/plans/${planId}/email-conversations/${conversationId}/reply`, { message });
  return data;
};

const updateInfluencer = async ({ planId, influencerId, ...edits }) => {
  const { data } = await axios.patch(`/plans/${planId}/influencers/${influencerId}`, edits);
  return data;
};

const refreshInfluencer = async ({ planId, influencerId }) => {
  const { data } = await axios.post(`/plans/${planId}/influencers/${influencerId}/refresh`);
  return data;
};

// Kicks off the background "Refresh All" job for one list — returns
// immediately with just the eligible count; the actual work happens in a
// BullMQ job (see helpers/mediaStatsQueue.js), tracked via refreshAllStatus.
const triggerRefreshAll = async ({ planId, listId }) => {
  const { data } = await axios.post(`/plans/${planId}/lists/${listId}/refresh-all`);
  return data;
};

const fetchRefreshAllStatus = async ({ planId, listId }) => {
  const { data } = await axios.get(`/plans/${planId}/lists/${listId}/refresh-status`);
  return data;
};

// deliverables is the full set of deliverables to show as Total Cost columns
// for this list (an array now, not a single value) — see the Total Cost
// dropdown checkboxes in InfluencerTable.jsx.
const bulkSetDeliverable = async ({ planId, listId, deliverables }) => {
  const { data } = await axios.patch(`/plans/${planId}/lists/${listId}/deliverables`, { deliverables });
  return data;
};

const setDeliverableCost = async ({ planId, influencerId, deliverable, cost }) => {
  const { data } = await axios.patch(`/plans/${planId}/influencers/${influencerId}/deliverable-cost`, {
    deliverable,
    cost,
  });
  return data;
};

// Same mechanism as bulkSetDeliverable/setDeliverableCost above, for the
// independent Brand Cost column group — see activeBrandDeliverables/
// brandDeliverableCosts in pluginbackendnode/models/plan.js.
const bulkSetBrandDeliverable = async ({ planId, listId, deliverables }) => {
  const { data } = await axios.patch(`/plans/${planId}/lists/${listId}/brand-deliverables`, { deliverables });
  return data;
};

const setBrandDeliverableCost = async ({ planId, influencerId, deliverable, cost }) => {
  const { data } = await axios.patch(`/plans/${planId}/influencers/${influencerId}/brand-deliverable-cost`, {
    deliverable,
    cost,
  });
  return data;
};

// Deliverable labels are a global pool, not scoped to one plan (see
// deliverables/custom routes in pluginbackendnode/routes/plans.js) — this IS
// the full Total Cost dropdown catalog now, no separate fixed list merged
// in on top of it.
const fetchCustomDeliverables = async () => {
  const { data } = await axios.get(`/plans/deliverables/custom`);
  return data.deliverables;
};

const createCustomDeliverable = async ({ name }) => {
  const { data } = await axios.post(`/plans/deliverables/custom`, { name });
  return data;
};

const deleteCustomDeliverable = async ({ name }) => {
  const { data } = await axios.delete(`/plans/deliverables/custom/${encodeURIComponent(name)}`);
  return data;
};

// Same mechanism as bulkSetDeliverable/setDeliverableCost above, for the
// independent freeform custom-field column group — see activeCustomFields/
// customFields in pluginbackendnode/models/plan.js.
const bulkSetCustomField = async ({ planId, listId, fields }) => {
  const { data } = await axios.patch(`/plans/${planId}/lists/${listId}/custom-fields`, { fields });
  return data;
};

const setCustomFieldValue = async ({ planId, influencerId, field, value }) => {
  const { data } = await axios.patch(`/plans/${planId}/influencers/${influencerId}/custom-field-value`, {
    field,
    value,
  });
  return data;
};

// Custom-field labels are a global pool, not scoped to one plan — same
// mechanism as fetchCustomDeliverables/createCustomDeliverable/
// deleteCustomDeliverable above, kept as an entirely separate catalog.
const fetchCustomFields = async () => {
  const { data } = await axios.get(`/plans/custom-fields`);
  return data.fields;
};

const createCustomField = async ({ name }) => {
  const { data } = await axios.post(`/plans/custom-fields`, { name });
  return data;
};

const deleteCustomField = async ({ name }) => {
  const { data } = await axios.delete(`/plans/custom-fields/${encodeURIComponent(name)}`);
  return data;
};

const setPlanBudget = async ({ planId, budget }) => {
  const { data } = await axios.patch(`/plans/${planId}/budget`, { budget });
  return data;
};

const setListBudget = async ({ planId, listId, budget }) => {
  const { data } = await axios.patch(`/plans/${planId}/lists/${listId}/budget`, { budget });
  return data;
};

// contactNumbers is the full replacement list for this influencer (mirrors how
// LanguageCell commits its whole array at once) — it's synced straight to the
// master RapidInfluencer record server-side, not stored on the plan itself.
const setContactNumbers = async ({ planId, influencerId, contactNumbers }) => {
  const { data } = await axios.patch(`/plans/${planId}/influencers/${influencerId}/contact-numbers`, {
    contactNumbers,
  });
  return data;
};

const setEmails = async ({ planId, influencerId, emails }) => {
  const { data } = await axios.patch(`/plans/${planId}/influencers/${influencerId}/emails`, {
    emails,
  });
  return data;
};

const downloadList = async ({ planId, listId }) => {
  const { data } = await axios.get(`/plans/${planId}/lists/${listId}/export`);
  return data;
};

export const usePlanSummary = (planId) => {
  return useQuery({
    queryKey: ["plan-summary", planId],
    queryFn: () => fetchPlanSummary(planId),
    enabled: Boolean(planId),
    staleTime: 5 * 60 * 1000,
  });
};

export const useAddInfluencers = (planId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => addInfluencers({ planId, ...payload }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["plan-summary", planId] });
    },
    onError: (error) => {
      console.error("Error adding influencers:", error);
      throw error;
    },
  });
};

export const useAddInfluencersByRapidId = (planId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => addInfluencersByRapidId({ planId, ...payload }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["plan-summary", planId] });
    },
    onError: (error) => {
      console.error("Error adding influencers by id:", error);
      throw error;
    },
  });
};

export const useCreateList = (planId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ name, platform }) => createList({ planId, name, platform }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["plan-summary", planId] });
    },
    onError: (error) => {
      console.error("Error creating list:", error);
      throw error;
    },
  });
};

export const useDeleteList = (planId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ listId }) => deleteList({ planId, listId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["plan-summary", planId] });
    },
    onError: (error) => {
      console.error("Error deleting list:", error);
      throw error;
    },
  });
};

export const useRemoveInfluencer = (planId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (influencerId) => removeInfluencer({ planId, influencerId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["plan-summary", planId] });
    },
    onError: (error) => {
      console.error("Error removing influencer:", error);
      throw error;
    },
  });
};

export const useRemoveInfluencers = (planId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (influencerIds) => removeInfluencers({ planId, influencerIds }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["plan-summary", planId] });
    },
    onError: (error) => {
      console.error("Error bulk-removing influencers:", error);
      throw error;
    },
  });
};

// Approved-only by default — the compose modal only ever offers templates that can
// actually be sent; pass undefined to list every status (used by an admin template
// management view, if one gets built).
export const useWhatsappTemplates = (status = "APPROVED") => {
  return useQuery({
    queryKey: ["whatsapp-templates", status],
    queryFn: () => fetchWhatsappTemplates(status),
    staleTime: 60 * 1000,
  });
};

export const useSendBulkWhatsappMessage = (planId) => {
  return useMutation({
    mutationFn: (payload) => sendBulkWhatsappMessage({ planId, ...payload }),
    onError: (error) => {
      console.error("Error bulk-sending WhatsApp message:", error);
      throw error;
    },
  });
};

// Call with an array of influencer subdocument ids for a specific selection, or the
// literal string "all" to mark the whole plan's current membership.
export const useOptInInfluencersWhatsapp = (planId) => {
  return useMutation({
    mutationFn: (influencerIdsOrAll) =>
      optInInfluencersWhatsapp(
        influencerIdsOrAll === "all" ? { planId, all: true } : { planId, influencerIds: influencerIdsOrAll }
      ),
    onError: (error) => {
      console.error("Error recording WhatsApp opt-in:", error);
      throw error;
    },
  });
};

export const usePlanWhatsappConversations = (planId) => {
  return useQuery({
    queryKey: ["plan-whatsapp-conversations", planId],
    queryFn: () => fetchPlanWhatsappConversations(planId),
    enabled: Boolean(planId),
    staleTime: 15 * 1000,
  });
};

export const usePlanWhatsappThread = (planId, conversationId) => {
  return useQuery({
    queryKey: ["plan-whatsapp-thread", planId, conversationId],
    queryFn: () => fetchPlanWhatsappThread({ planId, conversationId }),
    enabled: Boolean(planId) && Boolean(conversationId),
    staleTime: 10 * 1000,
  });
};

export const useSendPlanWhatsappMessage = (planId) => {
  return useMutation({
    mutationFn: ({ conversationId, text }) => sendPlanWhatsappMessage({ planId, conversationId, text }),
    onError: (error) => {
      console.error("Error sending plan WhatsApp message:", error);
      throw error;
    },
  });
};

export const useSendBulkEmail = (planId) => {
  return useMutation({
    mutationFn: (payload) => sendBulkEmail({ planId, ...payload }),
    onError: (error) => {
      console.error("Error bulk-sending email:", error);
      throw error;
    },
  });
};

export const usePlanEmailConversations = (planId) => {
  return useQuery({
    queryKey: ["plan-email-conversations", planId],
    queryFn: () => fetchPlanEmailConversations(planId),
    enabled: Boolean(planId),
    staleTime: 15 * 1000,
  });
};

export const usePlanEmailThread = (planId, conversationId) => {
  return useQuery({
    queryKey: ["plan-email-thread", planId, conversationId],
    queryFn: () => fetchPlanEmailThread({ planId, conversationId }),
    enabled: Boolean(planId) && Boolean(conversationId),
    staleTime: 10 * 1000,
  });
};

export const useSendPlanEmailReply = (planId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ conversationId, message }) => sendPlanEmailReply({ planId, conversationId, message }),
    onSuccess: (_, { conversationId }) => {
      queryClient.invalidateQueries({ queryKey: ["plan-email-thread", planId, conversationId] });
      queryClient.invalidateQueries({ queryKey: ["plan-email-conversations", planId] });
    },
    onError: (error) => {
      console.error("Error sending plan email reply:", error);
      throw error;
    },
  });
};

export const useUpdateInfluencer = (planId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (edits) => updateInfluencer({ planId, ...edits }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["plan-summary", planId] });
    },
    onError: (error) => {
      console.error("Error updating influencer:", error);
      throw error;
    },
  });
};

export const useRefreshInfluencer = (planId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ influencerId }) => refreshInfluencer({ planId, influencerId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["plan-summary", planId] });
    },
    onError: (error) => {
      console.error("Error refreshing influencer:", error);
      throw error;
    },
  });
};

// Fire-and-return — the mutation resolves as soon as the job is ENQUEUED
// (202), not when refreshing actually finishes. useRefreshAllStatus is what
// tracks real progress/completion afterward.
export const useTriggerRefreshAll = (planId) => {
  return useMutation({
    mutationFn: ({ listId }) => triggerRefreshAll({ planId, listId }),
    onError: (error) => {
      console.error("Error starting bulk refresh:", error);
      throw error;
    },
  });
};

// Polls the list's refresh-job status. `enabled` should be true only while a
// run is known (or suspected, e.g. right after mount) to be in flight —
// no polling at all the rest of the time.
export const useRefreshAllStatus = (planId, listId, { enabled = false } = {}) => {
  return useQuery({
    queryKey: ["refresh-all-status", planId, listId],
    queryFn: () => fetchRefreshAllStatus({ planId, listId }),
    enabled: Boolean(planId) && Boolean(listId) && enabled,
    refetchInterval: (data) => (data?.isRefreshing ? 2000 : false),
    // A one-shot check (e.g. on mount) shouldn't linger as "fresh" once the
    // component decides polling should stop — always allowed to refetch.
    staleTime: 0,
  });
};

export const useBulkSetDeliverable = (planId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ listId, deliverables }) => bulkSetDeliverable({ planId, listId, deliverables }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["plan-summary", planId] });
    },
    onError: (error) => {
      console.error("Error bulk-setting deliverable:", error);
      throw error;
    },
  });
};

export const useBulkSetBrandDeliverable = (planId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ listId, deliverables }) => bulkSetBrandDeliverable({ planId, listId, deliverables }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["plan-summary", planId] });
    },
    onError: (error) => {
      console.error("Error bulk-setting brand deliverable:", error);
      throw error;
    },
  });
};

// Same query key everywhere regardless of which plan/list is open, so
// creating a custom deliverable from one plan makes it show up in every
// other plan's dropdown too.
export const useCustomDeliverables = () => {
  return useQuery({
    queryKey: ["custom-deliverables"],
    queryFn: fetchCustomDeliverables,
    staleTime: 5 * 60 * 1000,
  });
};

export const useCreateCustomDeliverable = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createCustomDeliverable,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["custom-deliverables"] });
    },
    onError: (error) => {
      console.error("Error creating custom deliverable:", error);
      throw error;
    },
  });
};

// Backend 409s (and leaves the label untouched) if it's still referenced by
// any plan — the mutation surfaces that message as-is rather than a generic
// failure, so the caller can show the real reason.
export const useDeleteCustomDeliverable = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteCustomDeliverable,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["custom-deliverables"] });
    },
    onError: (error) => {
      console.error("Error deleting custom deliverable:", error);
      throw error;
    },
  });
};

export const useSetDeliverableCost = (planId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (edits) => setDeliverableCost({ planId, ...edits }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["plan-summary", planId] });
    },
    onError: (error) => {
      console.error("Error setting deliverable cost:", error);
      throw error;
    },
  });
};

export const useSetBrandDeliverableCost = (planId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (edits) => setBrandDeliverableCost({ planId, ...edits }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["plan-summary", planId] });
    },
    onError: (error) => {
      console.error("Error setting brand deliverable cost:", error);
      throw error;
    },
  });
};

export const useBulkSetCustomField = (planId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ listId, fields }) => bulkSetCustomField({ planId, listId, fields }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["plan-summary", planId] });
    },
    onError: (error) => {
      console.error("Error bulk-setting custom field:", error);
      throw error;
    },
  });
};

// Same query key everywhere regardless of which plan/list is open, so
// creating a custom field from one plan makes it show up in every other
// plan's dropdown too.
export const useCustomFields = () => {
  return useQuery({
    queryKey: ["custom-influencer-fields"],
    queryFn: fetchCustomFields,
    staleTime: 5 * 60 * 1000,
  });
};

export const useCreateCustomField = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createCustomField,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["custom-influencer-fields"] });
    },
    onError: (error) => {
      console.error("Error creating custom field:", error);
      throw error;
    },
  });
};

// Backend 409s (and leaves the label untouched) if it's still referenced by
// any plan — the mutation surfaces that message as-is rather than a generic
// failure, so the caller can show the real reason.
export const useDeleteCustomField = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteCustomField,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["custom-influencer-fields"] });
    },
    onError: (error) => {
      console.error("Error deleting custom field:", error);
      throw error;
    },
  });
};

export const useSetCustomFieldValue = (planId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (edits) => setCustomFieldValue({ planId, ...edits }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["plan-summary", planId] });
    },
    onError: (error) => {
      console.error("Error setting custom field value:", error);
      throw error;
    },
  });
};

export const useSetPlanBudget = (planId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (edits) => setPlanBudget({ planId, ...edits }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["plan-summary", planId] });
    },
    onError: (error) => {
      console.error("Error setting plan budget:", error);
      throw error;
    },
  });
};

export const useSetListBudget = (planId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (edits) => setListBudget({ planId, ...edits }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["plan-summary", planId] });
    },
    onError: (error) => {
      console.error("Error setting list budget:", error);
      throw error;
    },
  });
};

export const useSetContactNumbers = (planId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (edits) => setContactNumbers({ planId, ...edits }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["plan-summary", planId] });
    },
    onError: (error) => {
      console.error("Error setting contact numbers:", error);
      throw error;
    },
  });
};

export const useSetEmails = (planId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (edits) => setEmails({ planId, ...edits }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["plan-summary", planId] });
    },
    onError: (error) => {
      console.error("Error setting emails:", error);
      throw error;
    },
  });
};

export const useDownloadList = (planId) => {
  return useMutation({
    mutationFn: ({ listId }) => downloadList({ planId, listId }),
    onSuccess: (data) => {
      if (data?.downloadUrl) triggerBrowserDownload(data.downloadUrl);
    },
    onError: (error) => {
      console.error("Error downloading list:", error);
      throw error;
    },
  });
};

export default {
  usePlanSummary,
  useAddInfluencers,
  useCreateList,
  useDeleteList,
  useRemoveInfluencer,
  useRemoveInfluencers,
  useUpdateInfluencer,
  useRefreshInfluencer,
  useBulkSetDeliverable,
  useSetDeliverableCost,
  useBulkSetBrandDeliverable,
  useSetBrandDeliverableCost,
  useBulkSetCustomField,
  useSetCustomFieldValue,
  useSetContactNumbers,
  useDownloadList,
};
